import { useMemo, useState } from 'react'
import { isEmpty, last } from 'lodash-es'
import type { FeatureCollection, Point, Polygon as GeoJsonPolygon } from 'geojson'
import type {
  GeographicCoordinates,
  Polygon,
  RouteSegment,
  SignPostPlanningData,
  StreetNodePlanningData,
  StreetSegmentPlanningData,
  TechnicalSegmentPlanningData,
} from '@/api/main/types.gen'
import { NODE_VISIBLE_MIN_ZOOM, PLANNER_FALLBACK_ZOOM, SIGN_POST_ARROW_LENGTH_DEG } from './constants'
import { excludeOverlappingContinuationSegments } from './editTailContinuations'
import type { GapFix } from './gapFix'
import {
  boundsFromPoints,
  editTailToFeatureCollection,
  nodesToFeatureCollection,
  polygonToGeoJsonFeature,
  routeSegmentsToFeatureCollection,
  signPostAzimuthsToFeatureCollection,
  signPostsToFeatureCollection,
  streetSegmentsToFeatureCollection,
  technicalSegmentsToFeatureCollection,
  type LngLatBoundsLike,
} from './geojsonBuilders'
import { formatNodeLabel, pathVisitNumbers } from './nodeLabels'
import { collectRoutableNodeIds } from './segmentFilters'
import type { EditState, NodeFeatureProperties, PathState } from './types'

type UsePlannerMapDataArgs = {
  areaId: string
  planningAreaBounds: Polygon
  polygon: GeographicCoordinates[]
  pathState: PathState
  pathNodes: string[]
  editState: EditState | null
  indexedSegments: Record<string, StreetSegmentPlanningData>
  indexedAllSegments: Record<string, StreetSegmentPlanningData>
  streetNodes: Record<string, StreetNodePlanningData>
  signPosts: SignPostPlanningData[]
  technicalSegments: TechnicalSegmentPlanningData[]
  continuationSegments: StreetSegmentPlanningData[]
  nonBlockedStreetSegments: StreetSegmentPlanningData[]
  drivesBySegment: Record<string, { normal: number; reverse: number }>
  displayAllNodes: boolean
  displayOffNetworkNodes: boolean
  displaySignPosts: boolean
  displayParkingAreas: boolean
  isPathBuildMode: boolean
  addTarget: string | null
  shortestPathHint: RouteSegment[] | null
  hintReconnectNodeId: string | null
  hintVisitByNode: Map<string, number>
  hintReconnectPosition: number | undefined
  hintNodeIds: Set<string>
  pendingNodeIds: Set<string>
  routeGapNodeIds: Set<string>
  gapFixes: GapFix[]
  gapFixPreview: boolean
  reconnectIndex: number
}

export function usePlannerMapData({
  areaId,
  planningAreaBounds,
  polygon,
  pathState,
  pathNodes,
  editState,
  indexedSegments,
  indexedAllSegments,
  streetNodes,
  signPosts,
  technicalSegments,
  continuationSegments,
  nonBlockedStreetSegments,
  drivesBySegment,
  displayAllNodes,
  displayOffNetworkNodes,
  displaySignPosts,
  displayParkingAreas,
  isPathBuildMode,
  addTarget,
  shortestPathHint,
  hintReconnectNodeId,
  hintVisitByNode,
  hintReconnectPosition,
  hintNodeIds,
  pendingNodeIds,
  routeGapNodeIds,
  gapFixes,
  gapFixPreview,
  reconnectIndex,
}: UsePlannerMapDataArgs) {
  const [viewportBounds, setViewportBounds] = useState<LngLatBoundsLike | null>(null)
  const [zoom, setZoom] = useState(PLANNER_FALLBACK_ZOOM)

  const areaPolygon: FeatureCollection<GeoJsonPolygon> = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: [polygonToGeoJsonFeature(planningAreaBounds, { areaId })],
    }),
    [planningAreaBounds, areaId]
  )

  const parkingPolygonsGeo = useMemo(
    () =>
      displayParkingAreas
        ? technicalSegmentsToFeatureCollection(technicalSegments)
        : { type: 'FeatureCollection' as const, features: [] },
    [displayParkingAreas, technicalSegments]
  )

  const streetSegmentsGeo = useMemo(
    () =>
      streetSegmentsToFeatureCollection(Object.values(indexedSegments), {
        getProperties: segment => ({ isBidirectional: segment.is_bidirectional }),
      }),
    [indexedSegments]
  )

  const pathSegmentsGeo = useMemo(
    () => routeSegmentsToFeatureCollection(pathState.segments, indexedAllSegments),
    [pathState.segments, indexedAllSegments]
  )

  const continuationGeo = useMemo(() => {
    const segments = excludeOverlappingContinuationSegments(
      continuationSegments,
      pathState.segments,
      editState?.originalTail
    )
    return streetSegmentsToFeatureCollection(segments, {
      getProperties: (segment: StreetSegmentPlanningData) => {
        const reverseDrive = segment.source_node_id !== pathState.lastNode
        const previousDrives = drivesBySegment[segment.street_segment_id] ?? { normal: 0, reverse: 0 }
        const unnecessary = !reverseDrive
          ? previousDrives.normal >= segment.requested_drives
          : previousDrives.reverse >= segment.requested_drives
        return { unnecessary }
      },
    })
  }, [continuationSegments, pathState.segments, editState?.originalTail, pathState.lastNode, drivesBySegment])

  const shortestPathGeo = useMemo(
    () =>
      shortestPathHint
        ? routeSegmentsToFeatureCollection(shortestPathHint, indexedAllSegments, { hint: true })
        : { type: 'FeatureCollection' as const, features: [] },
    [shortestPathHint, indexedAllSegments]
  )

  const gapFixGeo = useMemo(() => {
    if (!gapFixPreview || isEmpty(gapFixes)) {
      return { type: 'FeatureCollection' as const, features: [] }
    }
    const bridgeSegments = gapFixes.flatMap(fix => fix.bridge)
    return routeSegmentsToFeatureCollection(bridgeSegments, indexedAllSegments, { gapFix: true })
  }, [gapFixPreview, gapFixes, indexedAllSegments])

  const editTailGeo = useMemo(() => {
    if (!editState) return { type: 'FeatureCollection' as const, features: [] }
    return editTailToFeatureCollection(editState.originalTail, reconnectIndex, indexedAllSegments)
  }, [editState, reconnectIndex, indexedAllSegments])

  const signPostsGeo = useMemo(
    () =>
      displaySignPosts
        ? signPostsToFeatureCollection(signPosts, viewportBounds)
        : { type: 'FeatureCollection' as const, features: [] },
    [displaySignPosts, signPosts, viewportBounds]
  )

  const signPostAzimuthsGeo = useMemo(
    () =>
      displaySignPosts
        ? signPostAzimuthsToFeatureCollection(signPosts, SIGN_POST_ARROW_LENGTH_DEG, viewportBounds)
        : { type: 'FeatureCollection' as const, features: [] },
    [displaySignPosts, signPosts, viewportBounds]
  )

  const routableNodeIds = useMemo(() => collectRoutableNodeIds(nonBlockedStreetSegments), [nonBlockedStreetSegments])

  const visibleNodes = useMemo(() => {
    const all = Object.values(streetNodes) as StreetNodePlanningData[]
    return all.filter(n => {
      const routable = routableNodeIds.has(n.street_node_id)
      if (routable) return displayAllNodes
      return displayOffNetworkNodes
    })
  }, [streetNodes, routableNodeIds, displayAllNodes, displayOffNetworkNodes])

  const nodesGeo: FeatureCollection<Point> = useMemo(() => {
    if (zoom < NODE_VISIBLE_MIN_ZOOM) {
      return { type: 'FeatureCollection', features: [] }
    }

    const alwaysIncludeIds = new Set<string>()
    for (const id of pathNodes) alwaysIncludeIds.add(id)
    for (const id of hintNodeIds) alwaysIncludeIds.add(id)
    for (const id of pendingNodeIds) alwaysIncludeIds.add(id)
    for (const id of routeGapNodeIds) alwaysIncludeIds.add(id)
    if (hintReconnectNodeId) alwaysIncludeIds.add(hintReconnectNodeId)
    if (addTarget) alwaysIncludeIds.add(addTarget)
    if (pathState.lastNode) alwaysIncludeIds.add(pathState.lastNode)
    for (const seg of continuationSegments) {
      alwaysIncludeIds.add(seg.source_node_id)
      alwaysIncludeIds.add(seg.destination_node_id)
    }
    if (editState) {
      for (const seg of editState.originalTail) {
        alwaysIncludeIds.add(seg.entrance_node_id)
        alwaysIncludeIds.add(seg.exit_node_id)
      }
    }

    const nodesById = new Map(visibleNodes.map(n => [n.street_node_id, n]))
    for (const id of alwaysIncludeIds) {
      const node = streetNodes[id]
      if (node && !nodesById.has(id)) nodesById.set(id, node)
    }
    const nodesForMap = [...nodesById.values()]

    const reconnectTargetNodeId = editState?.originalTail[0]?.exit_node_id
    const tailEndNodeId = last(editState?.originalTail ?? [])?.exit_node_id

    return nodesToFeatureCollection(
      nodesForMap,
      (node): NodeFeatureProperties => {
        const nodeId = node.street_node_id
        const visits = pathVisitNumbers(pathNodes, nodeId)
        const isHintEnd = nodeId === hintReconnectNodeId
        const hintVisit = isHintEnd ? hintReconnectPosition : hintVisitByNode.get(nodeId)
        const matchingSegment = continuationSegments.some(
          it => it.destination_node_id === nodeId || (it.source_node_id === nodeId && it.is_bidirectional)
        )
        const editablePositions = visits.filter(pos => pos < pathNodes.length)
        const lastEditablePos = editablePositions.at(-1)
        const isLastNode = nodeId === pathState.lastNode
        const isStartNode = pathNodes[0] !== undefined && nodeId === pathNodes[0]
        const isEditable = !editState && isPathBuildMode && lastEditablePos !== undefined && !isLastNode
        return {
          nodeId,
          label: formatNodeLabel(visits, hintVisit),
          isStartNode,
          isLastNode,
          isPathNode: !isEmpty(visits),
          isHintEnd,
          isHintNode: hintNodeIds.has(nodeId),
          isPendingRemoval: pendingNodeIds.has(nodeId),
          isContinuation: matchingSegment,
          isAddTarget: nodeId === addTarget,
          isGapNode: routeGapNodeIds.has(nodeId),
          isReconnectTarget: nodeId === reconnectTargetNodeId && !isPathBuildMode,
          isTailEnd: editState !== null && nodeId === tailEndNodeId,
          isEditable,
          isRoutable: routableNodeIds.has(nodeId),
        }
      },
      viewportBounds,
      alwaysIncludeIds
    )
  }, [
    zoom,
    visibleNodes,
    streetNodes,
    routableNodeIds,
    pathNodes,
    pathState.lastNode,
    continuationSegments,
    editState,
    isPathBuildMode,
    hintReconnectNodeId,
    hintReconnectPosition,
    hintVisitByNode,
    hintNodeIds,
    pendingNodeIds,
    routeGapNodeIds,
    addTarget,
    viewportBounds,
  ])

  const initialBounds = useMemo(() => boundsFromPoints(polygon), [polygon])

  return {
    viewportBounds,
    setViewportBounds,
    zoom,
    setZoom,
    areaPolygon,
    parkingPolygonsGeo,
    streetSegmentsGeo,
    pathSegmentsGeo,
    continuationGeo,
    shortestPathGeo,
    gapFixGeo,
    editTailGeo,
    signPostsGeo,
    signPostAzimuthsGeo,
    routableNodeIds,
    nodesGeo,
    initialBounds,
  }
}
